import React from 'react'
import { useTheme } from '@/hooks/useTheme'
import { useTiposEspacios } from '@/hooks/useTiposEspacios'
import ReportFilters from '@/components/reports/ReportFilters'
import OcupacionReport from '@/components/reports/OcupacionReport'
import IngresosReport from '@/components/reports/IngresosReport'
import EspaciosTopReport from '@/components/reports/EspaciosTopReport'
import MetodosPagoReport from '@/components/reports/MetodosPagoReport'
import ReservationsReportTable from '@/components/reports/ReservationsReportTable'
import PaymentsReportTable from '@/components/reports/PaymentsReportTable'
import { FiltrosReporte } from '@/services/reportService'
import { format, startOfMonth, endOfMonth } from 'date-fns'

type TabType = 'graficos' | 'informes'

const ReportsPage: React.FC = () => {
  const { colors } = useTheme()
  const { data: tiposEspacios = [], isLoading: loadingTipos } = useTiposEspacios()

  // Estado de filtros y tabs
  const [filtros, setFiltros] = React.useState<FiltrosReporte>({
    fecha_inicio: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
    fecha_fin: format(endOfMonth(new Date()), 'yyyy-MM-dd')
  })

  const [activeTab, setActiveTab] = React.useState<TabType>('graficos')
  const [refreshKey, setRefreshKey] = React.useState(0)

  const handleFiltrosChange = (newFiltros: FiltrosReporte) => {
    setFiltros(newFiltros)
  }

  const handleRefreshAll = () => {
    setRefreshKey((prev) => prev + 1)
  }

  return (
    <div>
      {/* Título de la página */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Reportes e Informes</h1>
        <p className="text-gray-600 mt-2">Análisis detallado de ocupación, ingresos, métodos de pago y operaciones</p>
      </div>

      {/* Filtros */}
      <div className="mb-8">
        <ReportFilters
          filtros={filtros}
          onFiltrosChange={handleFiltrosChange}
          tipos={tiposEspacios.map((tipo) => ({
            id: tipo.id,
            nombre: tipo.nombre
          }))}
          loadingTipos={loadingTipos}
        />
      </div>

      {/* Tabs */}
      <div className="mb-8">
        <div className="flex gap-4 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('graficos')}
            className="px-6 py-3 font-semibold transition-all border-b-2"
            style={{
              borderColor: activeTab === 'graficos' ? colors.primary : 'transparent',
              color: activeTab === 'graficos' ? colors.primary : '#666'
            }}
          >
            📊 Gráficos
          </button>
          <button
            onClick={() => setActiveTab('informes')}
            className="px-6 py-3 font-semibold transition-all border-b-2"
            style={{
              borderColor: activeTab === 'informes' ? colors.primary : 'transparent',
              color: activeTab === 'informes' ? colors.primary : '#666'
            }}
          >
            📋 Informes
          </button>
        </div>
      </div>

      {/* Botón de actualizar */}
      <div className="mb-8 flex justify-end">
        <button
          onClick={handleRefreshAll}
          className="px-4 py-2 rounded-lg font-semibold transition-all flex items-center gap-2"
          style={{ backgroundColor: colors.primary, color: 'white' }}
        >
          🔄 Actualizar
        </button>
      </div>

      {/* Contenido de Gráficos */}
      {activeTab === 'graficos' && (
        <div className="space-y-8">
          {/* Ocupación */}
          <div key={`ocupacion-${refreshKey}`}>
            <OcupacionReport filtros={filtros} onRefresh={handleRefreshAll} />
          </div>

          {/* Ingresos */}
          <div key={`ingresos-${refreshKey}`}>
            <IngresosReport filtros={filtros} onRefresh={handleRefreshAll} />
          </div>

          {/* Grid 2 columnas para los últimos 2 reportes */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Top espacios */}
            <div key={`espacios-${refreshKey}`}>
              <EspaciosTopReport filtros={filtros} onRefresh={handleRefreshAll} />
            </div>

            {/* Métodos de pago */}
            <div key={`metodos-${refreshKey}`}>
              <MetodosPagoReport filtros={filtros} onRefresh={handleRefreshAll} />
            </div>
          </div>

          {/* Info de ayuda */}
          <div className="mt-12 p-6 rounded-lg bg-blue-50 border border-blue-200">
            <p className="text-sm text-blue-900">
              💡 <strong>Tip:</strong> Usa los filtros para analizar periodos específicos. Los datos se actualizan
              automáticamente según los filtros seleccionados.
            </p>
          </div>
        </div>
      )}

      {/* Contenido de Informes */}
      {activeTab === 'informes' && (
        <div className="space-y-8">
          {/* Tabla de Reservas */}
          <div key={`reservas-${refreshKey}`}>
            <ReservationsReportTable filtros={filtros} />
          </div>

          {/* Tabla de Pagos */}
          <div key={`pagos-${refreshKey}`}>
            <PaymentsReportTable filtros={filtros} />
          </div>

          {/* Info de ayuda */}
          <div className="mt-12 p-6 rounded-lg bg-blue-50 border border-blue-200">
            <p className="text-sm text-blue-900">
              💡 <strong>Tip:</strong> Usa los filtros de fecha para filtrar por período. Las tablas muestran todos los
              registros del período seleccionado con paginación.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default ReportsPage
